// The demo replay cache (plan Task 21 step 3).
//
// Every language-model call in WINDTUNNEL is a pure function of `{model, system, user}` — the prompts
// carry the whole fight state, nothing is threaded through a conversation — so a run can be recorded
// once and replayed with no network at all. That is what makes the demo safe to show on conference
// wifi: with `VITE_LLM_CACHE=read` the advisors, the S-2 (intelligence officer) assessments, the FRAGO
// (fragmentary order) parser and the debrief narrative all answer from `public/demo/llm-cache.json`
// instead of the proxy, and even with the cache off a network failure falls back to it before it falls
// back to a template.
//
// Two stores sit behind one lookup:
//   * `public/demo/llm-cache.json` — the recorded run, fetched once and shared by every caller.
//   * `localStorage` — whatever this browser has seen since, so a second run of the same scenario is
//     instant and a recording session accumulates without a round trip to disk.
//
// Recording: `VITE_LLM_CACHE=record` keeps every successful answer in localStorage and
// `window.__wtDumpLlmCache()` prints the merged file to save over `public/demo/llm-cache.json`.

import { DEBUG } from '../debug';

/** Where the recorded demo run lives, relative to the site root. */
const DEMO_URL = '/demo/llm-cache.json';

/** The localStorage key holding this browser's own recordings. */
const LS_KEY = 'wt.llm.cache.v1';

/** Soft cap on the localStorage store, in characters. Oldest entries are dropped first. */
const LS_MAX_CHARS = 2_000_000;

export type CacheMode = 'off' | 'read' | 'record';

/** One recorded answer: the validated JSON the proxy returned, plus enough to read the file by eye. */
export interface CacheEntry {
  /** 'sonnet' or 'haiku' — the tier the recording was made against. */
  model: string;
  /** The first line of the user prompt, purely so a human can skim the file. */
  hint: string;
  /** The parsed JSON the model returned. */
  json: unknown;
}

export type CacheFile = Record<string, CacheEntry>;

/**
 * Overrides `VITE_LLM_CACHE` for the rest of this tab's life (pitch 8, "shareable fights"). The build-time
 * env var is fixed at bundle time, but opening a shared fight (`src/share/fight.ts`) wants this tab to
 * answer from the sharer's recorded answers even when the deployed build was not built with
 * `VITE_LLM_CACHE=read` - so `?fight=...` calls `setRuntimeCacheMode('read')` after seeding this browser's
 * store with the record's `llm` map, and every `cacheMode()` call downstream (`src/ai/client.ts`) sees it
 * without needing to know why.
 */
let runtimeMode: CacheMode | null = null;

/** Set by a shared-fight open; `null` restores whatever `VITE_LLM_CACHE` says. */
export function setRuntimeCacheMode(mode: CacheMode | null): void {
  runtimeMode = mode;
}

/** What `import.meta.env.VITE_LLM_CACHE` asks for, or the runtime override if one is set. Anything
 * unrecognised is 'off'. */
export function cacheMode(): CacheMode {
  if (runtimeMode) return runtimeMode;
  const raw = (import.meta.env?.VITE_LLM_CACHE ?? '').toString().trim().toLowerCase();
  return raw === 'read' || raw === 'record' ? raw : 'off';
}

/**
 * A stable 64-bit key for one call, as 16 hex characters.
 *
 * Two independent FNV-1a passes (different offset bases, the second over the reversed string) are
 * concatenated: one 32-bit hash collides at a few thousand entries by the birthday bound, which is the
 * same order as a long recording session, and a collision here would serve one advisor another
 * advisor's play. Cheap, deterministic, no dependency.
 */
export function cacheKey(req: { model: string; system: string; user: string }): string {
  const s = `${req.model}\u0000${req.system}\u0000${req.user}`;
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < s.length; i++) {
    a = Math.imul(a ^ s.charCodeAt(i), 0x01000193) >>> 0;
    b = Math.imul(b ^ s.charCodeAt(s.length - 1 - i), 0x85ebca6b) >>> 0;
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

// ---- the demo file --------------------------------------------------------------------------------

let demoPromise: Promise<CacheFile> | null = null;

/** Fetched once per page; a missing or malformed file is simply an empty cache, never an error. */
function demoFile(): Promise<CacheFile> {
  if (demoPromise) return demoPromise;
  demoPromise = (async () => {
    try {
      const res = await fetch(DEMO_URL, { cache: 'force-cache' });
      if (!res.ok) return {};
      const data: unknown = await res.json();
      if (!data || typeof data !== 'object') return {};
      const entries = (data as { entries?: unknown }).entries;
      return (entries && typeof entries === 'object' ? entries : data) as CacheFile;
    } catch {
      return {};
    }
  })();
  return demoPromise;
}

// ---- this browser's store -------------------------------------------------------------------------

/** Read straight through to localStorage: it is small, and a stale in-memory copy would lose writes
 * made by another tab of the same demo. Every access is guarded — private windows throw on access. */
function readLocal(): CacheFile {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const data: unknown = JSON.parse(raw);
    return data && typeof data === 'object' ? (data as CacheFile) : {};
  } catch {
    return {};
  }
}

/**
 * A read-only snapshot of this browser's own recordings, insertion order preserved (oldest first).
 * `src/share/fight.ts` reads the tail of this - the most recently recorded answers - to build a fight
 * record's optional `llm` map, capped at 32 KB, so a shared fight can narrate offline from the exact
 * answers the sharer saw.
 */
export function cacheSnapshot(): CacheFile {
  return { ...readLocal() };
}

function writeLocal(file: CacheFile): void {
  try {
    let text = JSON.stringify(file);
    if (text.length > LS_MAX_CHARS) {
      // Oldest first: object key order is insertion order for string keys, so dropping from the front
      // drops the oldest recordings.
      const keys = Object.keys(file);
      const trimmed: CacheFile = { ...file };
      for (const k of keys) {
        delete trimmed[k];
        text = JSON.stringify(trimmed);
        if (text.length <= LS_MAX_CHARS) break;
      }
    }
    localStorage.setItem(LS_KEY, text);
  } catch {
    // Quota, private browsing, or no storage at all: the demo file still carries the run.
  }
}

// ---- the lookup -------------------------------------------------------------------------------------

/**
 * The recorded answer for this call, or undefined. Checks this browser's own recordings first (they are
 * the fresher of the two) and then the shipped demo run.
 */
export async function cacheGet(key: string): Promise<unknown | undefined> {
  const local = readLocal()[key];
  if (local !== undefined) return local.json;
  const demo = (await demoFile())[key];
  return demo === undefined ? undefined : demo.json;
}

/** Records one successful answer in this browser's store. */
export function cachePut(key: string, entry: CacheEntry): void {
  const file = readLocal();
  file[key] = entry;
  writeLocal(file);
}

/** The first line of a prompt, clipped — the human-readable label in the saved file. */
export function promptHint(user: string): string {
  const first = user.split('\n').find((l) => l.trim().length > 0) ?? '';
  return first.trim().slice(0, 80);
}

// ---- recording ---------------------------------------------------------------------------------------

/**
 * Installs `window.__wtDumpLlmCache()`, which prints the merged cache (the shipped demo run plus
 * everything this browser has recorded) as the JSON to save over `public/demo/llm-cache.json`.
 *
 * Called once from `callLLM` the first time it runs in record mode, so nothing is attached to the
 * window in an ordinary demo.
 */
export function installDumpHook(): void {
  // Development and `?debug` only (src/debug.ts); a deployed build ships no cache console.
  if (!DEBUG) return;
  if (typeof window === 'undefined') return;
  const w = window as unknown as Record<string, unknown>;
  if (w.__wtDumpLlmCache) return;
  w.__wtDumpLlmCache = async (): Promise<string> => {
    const merged: CacheFile = { ...(await demoFile()), ...readLocal() };
    const text = JSON.stringify({ version: 1, entries: merged }, null, 0);
    // eslint-disable-next-line no-console
    console.log(`[wt] llm cache: ${Object.keys(merged).length} entries, ${text.length} chars`);
    return text;
  };
}

/** Clears this browser's recordings. Exposed for the console and for tests. */
export function cacheClearLocal(): void {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * Starts the demo file fetching before anything needs it.
 *
 * The point of the cache is the moment the network is gone, and a lookup made after the network went
 * away cannot fetch the file it wants to read from. `callLLM` calls this at module load, which on a
 * demo laptop is while the venue's wifi still works.
 */
export function warmCache(): void {
  void demoFile();
}
